/**
 * VAL-68-OWNER-RULINGS R2 — "The buyer can always ask for an upgrade, add options."
 *
 * After buying, "Your value" lists the higher options not yet bought (server: GET /payment
 * `upgrades`, lowest first, each with its charge from core/valuation/value_payment.
 * upgrade_charge_cents). Each button starts a normal Stripe checkout for that option; the
 * server refuses an option already held. Prices are TEST values, never presented as final.
 * Renders nothing while there is nothing to add (or the list cannot be read).
 *
 * ADD_TEXT is WORDING FOR OWNER APPROVAL (VAL-68-OWNER-RULINGS report).
 */
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { fetchPaymentInfo, startCheckout, type PaymentInfo } from '@/api/valuationPayment'
import { formatApiError } from '@/utils/httpErrors'
import { CLIENT_TEXT as PAY_TEXT, formatCents } from './_PayForValue'

export const ADD_TEXT = {
  title: 'Add options',
  button: 'Add for {amount}',
} as const

export function AddOptions({ engagementId }: { engagementId: string }) {
  const [info, setInfo] = useState<PaymentInfo | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchPaymentInfo(engagementId)
      .then(res => { if (live) setInfo(res) })
      .catch(() => { /* nothing to add is shown as nothing */ })
    return () => { live = false }
  }, [engagementId])

  const ups = info?.upgrades ?? []
  if (!info || ups.length === 0) return null
  const off = !info.payments_configured

  const add = async (tier: string) => {
    setBusy(tier)
    setError(null)
    try {
      window.location.assign(await startCheckout(engagementId, tier))
    } catch (err: unknown) {
      setBusy(null)
      setError(formatApiError(err))
    }
  }

  return (
    <Card data-testid="add-options">
      <CardHeader><CardTitle>{ADD_TEXT.title}</CardTitle></CardHeader>
      <CardContent className="space-y-4 text-sm text-pal-ink-body">
        {off && <p className="text-pal-warning-text">{PAY_TEXT['pay.notConfigured']}</p>}
        <div className="flex flex-wrap gap-3">
          {ups.map(o => (
            <div key={o.tier} className="rounded-lg border border-pal-line p-4" data-testid={`add-${o.tier}`}>
              <p className="font-medium text-pal-ink">{o.title ?? o.label}</p>
              {o.description && <p className="mt-1 max-w-xs text-pal-ink-soft">{o.description}</p>}
              <Button className="mt-3" variant="outline" disabled={off || busy !== null}
                      onClick={() => { void add(o.tier) }}>
                {ADD_TEXT.button.replace('{amount}', formatCents(o.amount_cents, o.currency))}
              </Button>
            </div>
          ))}
        </div>
        {!off && <p className="text-xs text-pal-ink-muted">{PAY_TEXT['pay.secure']}</p>}
        {error && <p className="text-pal-error-text">{error}</p>}
      </CardContent>
    </Card>
  )
}
